import {
  SHIPMENT_TRACKING_ENABLE_LOADING,
  SHIPMENT_TRACKING_SET_DATA,
  SHIPMENT_TRACKING_ERROR,
  SHIPMENT_TRACKING_FILTERS,
  SHIPMENT_TRACKING_SET_PAGINATION,
  SHIPMENT_TRACKING_RESET_PAGINATION,
  SHIPMENT_TRACKING_RESET_FILTERS,
  SHIPMENT_TRACKING_FETCHING_DATA,
  SHIPMENT_TRACKING_CHECK_CHANGED,
  SHIPMENT_TRACKING_SET_PREV_PAGINATION,
  SHIPMENT_TRACKING_SET_POLLING_REMAINING,
  SHIPMENT_TRACKING_SET_POLLING_DATA,
  SHIPMENT_TRACKING_BATCH_SIZE,
  SHIPMENT_TRACKING_EXIM_TYPE,
  SHIPMENT_TRACKING_SUMMARY_METRIC_UPDATE,
  SHIPMENT_DETAILS_SET_DATA,
  SHIPMENT_TRACKING_DETAILS_LOADING,
  SHIPMENT_DETAILS_ERROR,
  SHIPMENT_TRACKING_RESET_DETAIL_DATA,
  SHIPMENT_TRACKING_SEARCH_FILTERS,
  SET_MAP_LIST_VIEW,
  SET_MASTER_DATA,
  ADVISORY_FETCH_ALL,
} from '../actions/constants';
import moment from 'moment';
import { SHIPMENT_TYPE, viewToggle, getDefaultSearchType } from 'src/components/pages/Container/ContainerUtils';
import { isValidBoolean, StateMerger } from 'src/Library/Helper';
import {
  CONTAINER_DEFAULT_SORT_BY, BL_DEFAULT_SORT_BY,
  SHIPMENT_DEFAULT_SORT_BY,
} from 'src/constants/shipmentTracking';
import { DefaultSortBy } from 'src/Library/Types';

const initialPagination = {
  currentPageNumber: 1,
  resultPerPage: 25,
  nextOrPrev: 'first',
  isNextPresent: true,
  isBlNextPresent: true,
  isContainerNextPresent: true,
};

const getDefaultSortBy = (sortBy: string = '', descendingOrder: boolean = true): DefaultSortBy => {
  return ({
    descendingOrder,
    sortBy,
  });
};

const defaultShipmentState = {
  data: null,
  pagination: initialPagination,
  prevPagination: initialPagination,
  selectedItems: {},
  items: [],
  descendingOrder: true,
  view: viewToggle.LIST,
};

const BL = {
  ...defaultShipmentState,
  defaultSortBy: getDefaultSortBy(BL_DEFAULT_SORT_BY),
  sortBy: BL_DEFAULT_SORT_BY,
  isContainerLoading: true,
  errorMessage: null,
  errorStatus: null,
  status: null,
  statusWiseCount: {},
};
const HBL = {
  ...defaultShipmentState,
  defaultSortBy: getDefaultSortBy(BL_DEFAULT_SORT_BY),
  sortBy: BL_DEFAULT_SORT_BY,
  isContainerLoading: true,
  errorMessage: null,
  errorStatus: null,
  status: null,
  statusWiseCount: {},
};

const SHIPMENT = {
  ...defaultShipmentState,
  sortBy: SHIPMENT_DEFAULT_SORT_BY,
  defaultSortBy: getDefaultSortBy(SHIPMENT_DEFAULT_SORT_BY),
};

const CONTAINER = {
  ...defaultShipmentState,
  sortBy: CONTAINER_DEFAULT_SORT_BY,
  defaultSortBy: getDefaultSortBy(CONTAINER_DEFAULT_SORT_BY),
};

const getDefaultSearchFilters = (tabId: string) => ({
  options: [],
  searchType: getDefaultSearchType(tabId),
});

const initialState = {
  SHIPMENT,
  BL,
  HBL,
  CONTAINER,
  isFetchingData: false,
  error: null,
  items: [],
  selectedFilters: {
    startDate: moment().subtract(7, 'd').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    shipmentTypeFilter: 'ALL',
    timeFilter: '6M',
    tabId: 'SHIPMENT',
    isManuallyTracked: false,
    isComplete: false, // show only incomplete shipments by default
    blReceived: 'ALL',
    timeFilterType: 'created_at',
  },
  searchFilters: getDefaultSearchFilters(SHIPMENT_TYPE.SHIPMENT),
  trackingListRemaining: [],
  statusWiseCount: {},
  shipmentData: {},
  shipmentLoading: true,
  carrierAdvisories: [],
};

export default function (state = initialState, action) {
  switch (action.type) {
    case SHIPMENT_DETAILS_ERROR: return setDetailsError(action, state);
    case SHIPMENT_TRACKING_ERROR: return setError(action.data, state);
    case SHIPMENT_TRACKING_FILTERS: return filtersUpdate(action.data, state);
    case SHIPMENT_DETAILS_SET_DATA: return getDetailsData(action.shipmentData, state);
    case SHIPMENT_TRACKING_SET_DATA: return setData(action.data, state, action.isFetchingData);
    case SHIPMENT_TRACKING_EXIM_TYPE: return setEximType(action.data, state);
    case SHIPMENT_TRACKING_BATCH_SIZE: return setBatchSize(action.resultPerPage, state);
    case SHIPMENT_TRACKING_RESET_FILTERS: return resetFilters(action, state);
    case SHIPMENT_TRACKING_CHECK_CHANGED: return updateSelection(action.selectedItems, state);
    case SHIPMENT_TRACKING_ENABLE_LOADING: return setLoader(state, true);
    case SHIPMENT_TRACKING_SEARCH_FILTERS: return setSearchFilters(action.filters, state);
    case SHIPMENT_TRACKING_SET_PAGINATION: return updatePagination(action.data, state);
    case SHIPMENT_TRACKING_DETAILS_LOADING: return setLodingDetails(action.loading, state);
    case SHIPMENT_TRACKING_FETCHING_DATA:
    case SHIPMENT_TRACKING_SET_POLLING_DATA: return getUpdatedData(action.data, state);
    case SHIPMENT_TRACKING_RESET_PAGINATION: return resetPagination(state);
    case SHIPMENT_TRACKING_RESET_DETAIL_DATA: return getDetailsData({}, state);
    case SHIPMENT_TRACKING_SET_PREV_PAGINATION: return setPrevPagination(state);
    case SHIPMENT_TRACKING_SUMMARY_METRIC_UPDATE: return getSummaryCounts(action.data, state);
    case SHIPMENT_TRACKING_SET_POLLING_REMAINING: return getPollingRemaining(action.data, state);
    case SET_MASTER_DATA: return setDataFromMasterData(action.data, state);
    case ADVISORY_FETCH_ALL: return setAdvisoryData(action.data, state);
    case SET_MAP_LIST_VIEW: return setView(action.data, state);
    default: return state;
  }
}

function setView(view, state) {
  const { tabId } = state.selectedFilters;
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      view,
    }
  };
}

function setAdvisoryData(carrierAdvisories, state) {
  return {
    ...state,
    carrierAdvisories,
  };
}

function setSearchFilters(searchFilters, state) {
  return {
    ...state,
    searchFilters: {
      ...state.searchFilters,
      ...searchFilters,
    },
  };
}

function setDetailsError(action, state) {
  return {
    ...state,
    errorMessage: action.errorMessage,
    errorStatus: action.status,
  };
}

function setLodingDetails(shipmentLoading, state) {
  return {
    ...state,
    shipmentLoading,
  };
}

function getDetailsData(shipmentData, state) {
  return {
    ...state,
    shipmentData,
    shipmentLoading: false,
  };
}

function getSummaryCounts(statusWiseCount, state) {
  return {
    ...state,
    statusWiseCount,
  };
}

function setEximType(eximType, state) {
  const { tabId } = state.selectedFilters;
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      eximType,
    },
  };
}

function setBatchSize(resultPerPage, state) {
  const { tabId } = state.selectedFilters;
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      pagination: {
        ...initialPagination,
        resultPerPage,
      },
    },
  };
}

function getPollingRemaining(trackingListRemaining, state) {
  return {
    ...state,
    trackingListRemaining,
  };
}

function getUpdatedData(items, state) {
  const { tabId } = state.selectedFilters;
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      items,
    },
  };
}

function updateSelection(selectedItems, state) {
  const { tabId } = state.selectedFilters;
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      selectedItems,
    },
  };
}

function resetPagination(state) {
  const { tabId } = state.selectedFilters;
  return {
    ...state,
    searchFilters: getDefaultSearchFilters(tabId),
    [tabId]: {
      ...state[tabId],
      pagination: initialPagination,
      prevPagination: initialPagination,
    },
  };
}

function updatePagination(newPagination, state) {
  const { tabId } = state.selectedFilters;
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      pagination: getPagination(newPagination, state),
      prevPagination: getPagination(newPagination, state),
    },
  };
}

function getPagination(newPagination, state) {
  const { tabId } = state.selectedFilters;
  switch (tabId) {
    case SHIPMENT_TYPE.CONTAINER: return getContainerPagination(
      state.CONTAINER.pagination,
      newPagination,
    );
    case SHIPMENT_TYPE.HBL: return getBLPagination(state.HBL.pagination, newPagination);
    case SHIPMENT_TYPE.BL: return getBLPagination(state.BL.pagination, newPagination);
    case SHIPMENT_TYPE.SHIPMENT: return getShipmentPagination(
      state.SHIPMENT.pagination,
      newPagination,
    );
    default: return initialPagination;
  }
}

function resetFilters(action, state) {
  const {
    tabId,
    pageOnly,
  } = action;
  const newPagination = {
    ...initialPagination,
    resultPerPage: state[tabId].pagination.resultPerPage,
  };

  const updates = pageOnly ? {
    isComplete: state.selectedFilters.isComplete,
    statusList: state.selectedFilters.statusList,
  } : {};
  return {
    ...state,
    selectedFilters: {
      ...initialState.selectedFilters,
      ...updates,
      tabId,
    },
    searchFilters: getDefaultSearchFilters(tabId),
    [tabId]: {
      ...state[tabId],
      selectedItems: {},
      sortBy: state[tabId].defaultSortBy.sortBy,
      descendingOrder: state[tabId].defaultSortBy.descendingOrder,
      pagination: newPagination,
      prevPagination: newPagination,
    },
  };
}

function setPrevPagination(state) {
  const { tabId } = state.selectedFilters;
  return {
    ...state,
    [tabId]: {
      ...state[tabId],
      pagination: state[tabId].prevPagination || initialPagination,
    },
  };
}

function filtersUpdate(filters, state) {
  let { tabId } = state.selectedFilters;
  let { searchFilters } = state;
  if (!tabId) {
    tabId = filters.tabId;
    searchFilters = getDefaultSearchFilters(tabId);
  }
  const { descendingOrder: defaultDescendingOrder } = state[tabId];
  const { descendingOrder = defaultDescendingOrder } = filters;
  const newPagination = {
    ...initialPagination,
    resultPerPage: state[tabId].pagination.resultPerPage,
  };
  return {
    ...state,
    searchFilters,
    selectedFilters: {
      ...state.selectedFilters,
      ...filters,
    },
    [tabId]: {
      ...state[tabId],
      descendingOrder,
      pagination: newPagination,
      prevPagination: newPagination,
      sortBy: filters.sortBy || state[tabId].sortBy,
    },
  };
}

function setLoader(state, isFetchingData) {
  return {
    ...state,
    isFetchingData,
    error: null,
  };
}

function setError(actionData, state) {
  return {
    ...state,
    isFetchingData: false,
    error: actionData,
  };
}

function setData(data, state, isFetchingData = false) {
  const { tabId } = state.selectedFilters;
  switch (tabId) {
    case SHIPMENT_TYPE.SHIPMENT: return getShipmentData(data, state, isFetchingData);
    case SHIPMENT_TYPE.CONTAINER: return getContainerData(data, state, isFetchingData);
    case SHIPMENT_TYPE.BL: return getBLData(data, state, isFetchingData);
    case SHIPMENT_TYPE.HBL: return getHBLData(data, state, isFetchingData);
    default: return state;
  }
}

function getCommonData(state, isFetchingData) {
  return {
    ...state,
    isFetchingData: isFetchingData || false,
    error: null,
  };
}

function getHBLData(hblData, state, isFetchingData) {
  const newPagination = getBLPagination(state.HBL.pagination, hblData);
  return {
    ...getCommonData(state, isFetchingData),
    HBL: {
      ...state.HBL,
      isTrackingRequestLoading: false,
      data: hblData,
      items: hblData.data,
      pagination: newPagination,
      prevPagination: state.HBL.pagination,
      statusWiseCount: hblData.statusWiseCount,
    },
  };
}

function getBLData(blData, state, isFetchingData) {
  const newPagination = getBLPagination(state.BL.pagination, blData);
  return {
    ...getCommonData(state, isFetchingData),
    BL: {
      ...state.BL,
      isTrackingRequestLoading: false,
      data: blData,
      items: blData.data,
      pagination: newPagination,
      prevPagination: state.BL.pagination,
      statusWiseCount: blData.statusWiseCount,
    },
  };
}

function getContainerData(containerData, state, isFetchingData) {
  const newPagination = getContainerPagination(state.CONTAINER.pagination, containerData);
  return {
    ...getCommonData(state, isFetchingData),
    CONTAINER: {
      ...state.CONTAINER,
      prevPagination: state.CONTAINER.pagination,
      pagination: newPagination,
      items: containerData.containerList,
      data: containerData,
    },
  };
}

function getShipmentData(shipmentData, state, isFetchingData) {
  const items = shipmentData.data || [];
  //delete shipmentData.data;
  const newPagination = getShipmentPagination(state.SHIPMENT.pagination, shipmentData);
  return {
    ...getCommonData(state, isFetchingData),
    SHIPMENT: {
      ...state.SHIPMENT,
      items,
      prevPagination: state.SHIPMENT.pagination,
      pagination: newPagination,
      data: {
        ...state.SHIPMENT.data,
        ...shipmentData,
      }
    },
  };
}

function getContainerPagination(pagination, newPagination) {
  const updatedPagination = {
    ...pagination,
    ...newPagination,
    containerList: undefined,
    statusWiseCount: undefined,
  };
  return updatedPagination;
}

function getBLPagination(pagination, newPagination) {
  const updatedPagination = {
    ...pagination,
    ...newPagination,
    data: undefined,
    statusWiseCount: undefined,
  };
  return updatedPagination;
}

function getShipmentPagination(pagination, newPagination) {
  const { isNextPresent } = newPagination;
  const updatedPagination = {
    ...pagination,
    ...newPagination,
    isNextPresent,
    shipmentList: undefined,
    statusWiseCount: undefined,
  };
  return updatedPagination;
}

const getUpdatedShipmentSortState = (state, defaults) => {
  const { descendingOrder, sortBy } = defaults || {};
  return {
    ...state,
    descendingOrder: isValidBoolean(descendingOrder) ? descendingOrder : state.descendingOrder,
    sortBy: sortBy || state.sortBy,
    defaultSortBy: defaults || state.defaultSortBy,
  };
};

function setDataFromMasterData(masterData, state) {
  const { sortBy } = masterData || {};
  return {
    ...state,
    SHIPMENT: getUpdatedShipmentSortState(state.SHIPMENT, sortBy?.SHIPMENT),
    BL: getUpdatedShipmentSortState(state.BL, sortBy?.BL),
    HBL: getUpdatedShipmentSortState(state.HBL, sortBy?.HBL),
    CONTAINER: getUpdatedShipmentSortState(state.CONTAINER, sortBy?.CONTAINER),
  };
}
